import { sanitizeChatMessage } from '../../utils/safeHtml';
import { ticketCommentToHtml } from '../../utils/plainTextToHtml';
import { chatAttachmentUrl } from '../../utils/chatAttachment';
import React from 'react';
import { createPortal } from 'react-dom';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Avatar,
    Divider,
    Paper,
    Grid,
    IconButton,
    Collapse,
    Button
} from '@mui/material';
import {
    Person,
    SupportAgent,
    Settings,
    Star,
    Close,
    Notifications,
    Image,
    AudioFile,
    VideoFile,
    Description,
    AttachFile,
    ExpandMore,
    ExpandLess,
    Download,
    Visibility,
    ChatBubbleOutlineOutlined
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import CustomAudioPlayer from '../CustomAudioPlayer';
import FilePreview from '../FilePreview';

const ChatTranscription = ({ comment, roomId }) => {
    const theme = useTheme();
    const [expanded, setExpanded] = React.useState(true);
    const [selectedFile, setSelectedFile] = React.useState(null);

    // Função para parsear o JSON da transcrição
    const parseTranscription = (commentText) => {
        try {
            return JSON.parse(commentText);
        } catch (error) {
            console.error('Erro ao parsear transcrição:', error);
            return null;
        }
    };

    // Função para obter ícone do tipo de remetente
    const getSenderIcon = (type) => {
        switch (type) {
            case 0: // Cliente
                return <Person sx={{ fontSize: 20 }} />;
            case 1: // Operador
                return <SupportAgent sx={{ fontSize: 20 }} />;
            case 2: // Sistema
                return <Settings sx={{ fontSize: 20 }} />;
            case 3: // Avaliação
                return <Star sx={{ fontSize: 20 }} />;
            case 4: // Encerramento
                return <Close sx={{ fontSize: 20 }} />;
            case 5: // Notificação
                return <Notifications sx={{ fontSize: 20 }} />;
            default:
                return <Person sx={{ fontSize: 20 }} />;
        }
    };

    // Função para obter ícone do tipo de arquivo
    const getFileIcon = (fileType) => {
        switch (fileType) {
            case 'image':
                return <Image sx={{ fontSize: 16, mr: 1 }} />;
            case 'audio':
                return <AudioFile sx={{ fontSize: 16, mr: 1 }} />;
            case 'video':
                return <VideoFile sx={{ fontSize: 16, mr: 1 }} />;
            case 'document':
                return <Description sx={{ fontSize: 16, mr: 1 }} />;
            default:
                return <AttachFile sx={{ fontSize: 16, mr: 1 }} />;
        }
    };

    // Função para obter cor do tipo de remetente
    const getSenderColor = (type) => {
        switch (type) {
            case 0: // Cliente
                return theme.palette.primary.main;
            case 1: // Operador
                return theme.palette.success.main;
            case 2: // Sistema
                return theme.palette.grey[600];
            case 3: // Avaliação
                return theme.palette.warning.main;
            case 4: // Encerramento
                return theme.palette.error.main;
            case 5: // Notificação
                return theme.palette.info.main;
            default:
                return theme.palette.grey[500];
        }
    };

    // Função para construir URL de download baseada no tipo de arquivo
    const getFileDownloadUrl = (fileInfo) => {
        if (!fileInfo || !fileInfo.filename) return null;

        // Determina o diretório baseado no tipo de arquivo
        let directory = '';
        switch (fileInfo.type) {
            case 'audio':
                directory = 'audios';
                break;
            case 'image':
                directory = 'images';
                break;
            case 'video':
                directory = 'videos';
                break;
            case 'document':
            default:
                directory = 'archives';
                break;
        }

        return chatAttachmentUrl(roomId, `${directory}/${fileInfo.filename}`);
    };

    // Função para verificar se é PDF
    const isPdfFile = (fileInfo) => {
        if (!fileInfo || !fileInfo.filename) return false;
        return /\.pdf$/i.test(fileInfo.filename) || fileInfo.type === 'document';
    };

    // Função para abrir preview do arquivo
    const handleFilePreview = (fileInfo) => {
        const fileUrl = getFileDownloadUrl(fileInfo);
        if (fileUrl) {
            setSelectedFile(fileUrl);
        }
    };

    const transcription = parseTranscription(comment);

    if (!transcription) {
        // Fallback para comentários que não são transcrições.
        //
        // O `ticketCommentToHtml` porque aqui só chega quem começa com `{` e não
        // é JSON válido -- ou seja, texto. Sem ele a quebra de linha some, do
        // mesmo jeito que sumia nas ações importadas do Movidesk.
        return (
            <Box sx={{
                backgroundColor: theme.palette.background.paper,
                padding: 2.5,
                borderRadius: 2,
                position: 'relative'
            }}>
                <div dangerouslySetInnerHTML={{ __html: sanitizeChatMessage(ticketCommentToHtml(comment)) }} />
            </Box>
        );
    }

    const { chat_info, messages } = transcription;

    return (
        <>
            <Paper elevation={3} sx={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 2,
                overflow: 'hidden',
                border: `2px solid ${theme.palette.primary.main}`,
                position: 'relative'
            }}>
                {/* Header da transcrição */}
                <Box sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                    color: 'white',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            color: 'white'
                        }}>
                            <ChatBubbleOutlineOutlined />
                        </Avatar>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Transcrição do Chat
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                Gerada automaticamente em {chat_info.generated_at}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={() => setExpanded(!expanded)}
                        sx={{ color: 'white' }}
                    >
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    {/* Informações do chat */}
                    <Box sx={{ p: 3, backgroundColor: theme.palette.background.default }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Person sx={{ color: theme.palette.primary.main }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Cliente:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {chat_info.client_name}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <SupportAgent sx={{ color: theme.palette.success.main }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Operador:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                        {chat_info.operator_name}
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Data de Início: {chat_info.start_date}
                                </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Typography variant="body2" color="textSecondary">
                                    Total de Mensagens: {chat_info.total_messages}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Box>

                    <Divider />

                    {/* Mensagens */}
                    <Box sx={{ p: 2 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.secondary, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <ChatBubbleOutlineOutlined />
                            Mensagens da Conversa
                        </Typography>

                        <Box sx={{
                            maxHeight: '400px',
                            overflowY: 'auto',
                            '&::-webkit-scrollbar': {
                                width: '8px',
                            },
                            '&::-webkit-scrollbar-track': {
                                backgroundColor: theme.palette.background.default,
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: theme.palette.divider,
                                borderRadius: '4px',
                            },
                        }}>
                            {messages.map((message, index) => {
                                const fileInfo = message.file_info;

                                return (
                                    <Card
                                        key={message.id || index}
                                        sx={{
                                            mb: 2,
                                            borderLeft: `4px solid ${getSenderColor(message.sender_type)}`,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        <CardContent sx={{ pb: 2 }}>
                                            {/* Header da mensagem */}
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                mb: 2
                                            }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar sx={{
                                                        width: 32,
                                                        height: 32,
                                                        backgroundColor: getSenderColor(message.sender_type),
                                                        color: 'white'
                                                    }}>
                                                        {getSenderIcon(message.sender_type)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {message.sender_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            {message.timestamp}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            {/* Conteúdo da mensagem */}
                                            <Box sx={{
                                                backgroundColor: theme.palette.background.default,
                                                p: 2,
                                                borderRadius: 1,
                                                border: `1px solid ${theme.palette.divider}`
                                            }}>
                                                {fileInfo ? (
                                                    <Box>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                            {getFileIcon(fileInfo.type)}
                                                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                                {message.content}
                                                            </Typography>
                                                        </Box>
                                                        {fileInfo.caption && (
                                                            <Box sx={{ mt: 1, p: 1, backgroundColor: theme.palette.background.paper, borderRadius: 1 }}>
                                                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                    <ChatBubbleOutlineOutlined sx={{ flexShrink: 0 }} />
                                                                    {fileInfo.caption}
                                                                </Typography>
                                                            </Box>
                                                        )}

                                                        {fileInfo.type === 'audio' && (
                                                            <Box sx={{ mt: 2 }}>
                                                                <CustomAudioPlayer archive={`audios/${message.file_info.filename}`} roomId={roomId} />
                                                            </Box>
                                                        )}

                                                        {(() => {
                                                            const fileUrl = getFileDownloadUrl(fileInfo);
                                                            const isPdf = isPdfFile(fileInfo);
                                                            
                                                            return fileUrl && (
                                                                <Box sx={{ mt: 2 }}>
                                                                    {isPdf && (
                                                                        <Box 
                                                                            sx={{ 
                                                                                mt: 1,
                                                                                mb: 1,
                                                                                border: `1px solid ${theme.palette.divider}`,
                                                                                borderRadius: 1,
                                                                                overflow: 'hidden',
                                                                                cursor: 'pointer',
                                                                                '&:hover': {
                                                                                    boxShadow: theme.shadows[2],
                                                                                    borderColor: theme.palette.primary.main
                                                                                }
                                                                            }}
                                                                            onClick={() => handleFilePreview(fileInfo)}
                                                                        >
                                                                            <Box sx={{ 
                                                                                position: 'relative',
                                                                                width: '100%',
                                                                                height: '300px',
                                                                                backgroundColor: theme.palette.background.paper
                                                                            }}>
                                                                                <iframe
                                                                                    src={fileUrl}
                                                                                    style={{ 
                                                                                        width: '100%', 
                                                                                        height: '100%', 
                                                                                        border: 'none',
                                                                                        pointerEvents: 'none'
                                                                                    }}
                                                                                    title="Mini visualização PDF"
                                                                                />
                                                                                <Box sx={{
                                                                                    position: 'absolute',
                                                                                    top: 0,
                                                                                    left: 0,
                                                                                    right: 0,
                                                                                    bottom: 0,
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                                                    '&:hover': {
                                                                                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                                                                                    }
                                                                                }}>
                                                                                    <Box sx={{
                                                                                        backgroundColor: theme.palette.primary.main,
                                                                                        color: 'white',
                                                                                        px: 2,
                                                                                        py: 1,
                                                                                        borderRadius: 1,
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        gap: 1
                                                                                    }}>
                                                                                        <Visibility sx={{ fontSize: 18 }} />
                                                                                        <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
                                                                                            Clique para visualizar em tamanho maior
                                                                                        </Typography>
                                                                                    </Box>
                                                                                </Box>
                                                                            </Box>
                                                                        </Box>
                                                                    )}
                                                                    <Button
                                                                        component="a"
                                                                        href={fileUrl}
                                                                        download={fileInfo.filename}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        variant="contained"
                                                                        size="small"
                                                                        startIcon={<Download />}
                                                                    >
                                                                        Baixar arquivo
                                                                    </Button>
                                                                </Box>
                                                            );
                                                        })()}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2">
                                                        {message.content}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </Box>
                    </Box>
                </Collapse>
            </Paper>

            {/* Preview de arquivo (PDF) usando Portal para renderizar fora do Paper e centralizado */}
            {selectedFile && typeof document !== 'undefined' && createPortal(
                <FilePreview
                    selectedFile={selectedFile}
                    setSelectedFile={setSelectedFile}
                    isChat={true}
                />,
                document.body
            )}
        </>
    );
};

export default ChatTranscription;
